import { useEffect, useState } from 'react';

// CookieYes writes the visitor's choice into its own cookie as a flat, comma-separated
// `key:value` list, e.g.
//   cookieyes-consent=consentid:abc,consent:yes,action:yes,necessary:yes,
//                     functional:yes,analytics:no,performance:yes,advertisement:no
// and fires `cookieyes_consent_update` on `document` whenever it changes. `action:yes`
// means the visitor has actually answered the banner — before that, nothing non-essential
// may run, regardless of what the category flags say.
const CONSENT_COOKIE = 'cookieyes-consent';
const CONSENT_EVENT = 'cookieyes_consent_update';

export type ConsentCategory = 'functional' | 'analytics' | 'performance' | 'advertisement';

export type CookieConsent = Record<ConsentCategory, boolean>;

const NO_CONSENT: CookieConsent = {
  functional: false,
  analytics: false,
  performance: false,
  advertisement: false,
};

const readConsent = (): CookieConsent => {
  if (typeof document === 'undefined') {
    return NO_CONSENT;
  }

  const raw = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${CONSENT_COOKIE}=`));

  if (!raw) {
    return NO_CONSENT;
  }

  const entries = new Map(
    decodeURIComponent(raw.slice(CONSENT_COOKIE.length + 1))
      .split(',')
      .map((pair) => {
        const [key, value] = pair.split(':');
        return [key?.trim() ?? '', value?.trim() ?? ''] as const;
      }),
  );

  // No explicit answer yet — treat as refusal.
  if (entries.get('action') !== 'yes') {
    return NO_CONSENT;
  }

  return {
    functional: entries.get('functional') === 'yes',
    analytics: entries.get('analytics') === 'yes',
    performance: entries.get('performance') === 'yes',
    advertisement: entries.get('advertisement') === 'yes',
  };
};

/**
 * Current CookieYes consent state. Always returns "nothing granted" on the server and on the
 * first client render, so a tag gated on this can never be mounted into the SSR output or
 * before the visitor has answered the banner.
 */
export const useCookieConsent = (): CookieConsent => {
  const [consent, setConsent] = useState<CookieConsent>(NO_CONSENT);

  useEffect(() => {
    // readConsent() builds a fresh object, and this hook lives in _app: an unconditional
    // setState here re-rendered the whole page on every window focus (e.g. the tap that
    // brings the tab back). Keep the previous object when nothing actually changed.
    const sync = () =>
      setConsent((previous) => {
        const next = readConsent();
        const isSame = (Object.keys(next) as ConsentCategory[]).every((key) => previous[key] === next[key]);
        return isSame ? previous : next;
      });

    sync();

    document.addEventListener(CONSENT_EVENT, sync);
    // Consent can also be revoked in another tab, which only shows up as a cookie change.
    window.addEventListener('focus', sync);

    return () => {
      document.removeEventListener(CONSENT_EVENT, sync);
      window.removeEventListener('focus', sync);
    };
  }, []);

  return consent;
};
